/**
 * AddImageButtonsItemDialog - Dialog para adicionar item de imagem com botões
 * 
 * Permite configurar:
 * - Imagem (via galeria ou URL externa)
 * - Texto da mensagem (obrigatório conforme Z-API)
 * - Até 3 botões de ação
 * 
 * IMPORTANTE: O id do botão é igual ao label para que quando Z-API retornar
 * o buttonId, já venha o texto legível (evita queries adicionais no webhook)
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, X, Image, Link2, GripVertical } from 'lucide-react';
import { MediaGalleryPicker } from '@/components/tenant/media/MediaGalleryPicker';
import { MessageItem, ButtonsConfig, CampaignButton } from './types';
import { useMediaGallery } from '@/hooks/useMediaGallery';
import { createLogger } from '@/lib/logger';

const logger = createLogger('AddImageButtonsItemDialog');

// =====================================================
// INTERFACES
// =====================================================

interface AddImageButtonsItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: {
    mediaUrl: string;
    mediaFilename?: string;
    mediaMimeType?: string;
    textContent: string;
    buttonsConfig: ButtonsConfig;
  }) => void;
  initialValue?: MessageItem | null;
  tenantId?: string;
}

// Máximo de botões permitido pelo WhatsApp
const MAX_BUTTONS = 3;

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

export function AddImageButtonsItemDialog({
  open,
  onOpenChange,
  onSave,
  initialValue,
  tenantId,
}: AddImageButtonsItemDialogProps) {
  const { getMediaUrl } = useMediaGallery({});
  
  // Estado do formulário
  const [mediaSource, setMediaSource] = useState<'gallery' | 'url'>('gallery');
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaFilename, setMediaFilename] = useState('');
  const [mediaMimeType, setMediaMimeType] = useState('');
  const [textContent, setTextContent] = useState('');
  // Usa array de labels apenas (id será igual ao label ao salvar)
  const [buttonLabels, setButtonLabels] = useState<string[]>(['']);
  
  // Estado do picker de galeria
  const [galleryOpen, setGalleryOpen] = useState(false);

  // Carregar valores iniciais quando editando
  useEffect(() => {
    if (open && initialValue) {
      setMediaUrl(initialValue.media_url || '');
      setMediaFilename(initialValue.media_filename || '');
      setMediaMimeType(initialValue.media_mime_type || '');
      setTextContent(initialValue.text_content || '');
      
      // Carregar labels dos botões existentes
      if (initialValue.buttons_config?.buttons?.length) {
        setButtonLabels(initialValue.buttons_config.buttons.map(btn => btn.label));
      } else {
        setButtonLabels(['']);
      }
      
      // Detectar fonte da mídia
      if (initialValue.media_url?.startsWith('http')) {
        setMediaSource('url');
      } else {
        setMediaSource('gallery');
      }
    } else if (open) {
      // Resetar para novo item
      setMediaUrl('');
      setMediaFilename('');
      setMediaMimeType('');
      setTextContent('');
      setButtonLabels(['']);
      setMediaSource('gallery');
    }
  }, [open, initialValue]);

  // =====================================================
  // HANDLERS
  // =====================================================

  /**
   * Adiciona novo botão à lista
   */
  const handleAddButton = () => {
    if (buttonLabels.length >= MAX_BUTTONS) return;
    setButtonLabels([...buttonLabels, '']);
  };

  /**
   * Remove botão da lista por índice
   */
  const handleRemoveButton = (index: number) => {
    if (buttonLabels.length <= 1) return; // Manter pelo menos 1 botão
    setButtonLabels(buttonLabels.filter((_, i) => i !== index));
  };

  /**
   * Atualiza label de um botão por índice
   */
  const handleUpdateButtonLabel = (index: number, label: string) => {
    setButtonLabels(buttonLabels.map((lbl, i) => i === index ? label : lbl));
  };

  /**
   * Seleciona mídia da galeria
   */
  const handleSelectFromGallery = (media: {
    storage_path: string;
    name: string;
    mime_type: string;
  }) => {
    setMediaUrl(media.storage_path);
    setMediaFilename(media.name);
    setMediaMimeType(media.mime_type);
    setGalleryOpen(false);
  };

  /**
   * Salva o item - Usa label como id do botão para evitar queries no webhook
   */
  const handleSave = () => {
    // Validações
    if (!mediaUrl.trim()) {
      logger.warn('Tentativa de salvar sem imagem');
      return;
    }
    if (!textContent.trim()) {
      logger.warn('Tentativa de salvar sem texto');
      return;
    }
    
    // Filtrar botões vazios e criar estrutura com id = label
    const validButtons: CampaignButton[] = buttonLabels
      .filter(label => label.trim())
      .map(label => ({ 
        id: label.trim(), // ID é o próprio label - Z-API retorna isso como buttonId
        label: label.trim() 
      }));
    
    if (validButtons.length === 0) {
      logger.warn('Tentativa de salvar sem botões válidos');
      return;
    }

    logger.debug('Salvando item de imagem com botões', { 
      mediaUrl, 
      textContent, 
      buttonsCount: validButtons.length,
      buttons: validButtons
    });

    onSave({
      mediaUrl,
      mediaFilename: mediaFilename || undefined,
      mediaMimeType: mediaMimeType || undefined,
      textContent,
      buttonsConfig: { buttons: validButtons },
    });
  };

  // Validação para habilitar botão salvar
  const isValid = 
    mediaUrl.trim() && 
    textContent.trim() && 
    buttonLabels.some(label => label.trim());

  // URL de preview da imagem
  const previewUrl = mediaUrl?.startsWith('http') 
    ? mediaUrl 
    : getMediaUrl(mediaUrl);

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>🖼️🔘</span>
              {initialValue ? 'Editar' : 'Adicionar'} Imagem com Botões
            </DialogTitle>
          </DialogHeader>

          {/* Container scrollável com altura calculada para garantir scroll funcional */}
          <div 
            className="flex-1 overflow-y-auto pr-4" 
            style={{ maxHeight: 'calc(90vh - 180px)' }}
          >
            <div className="space-y-4 py-2">
              {/* Seleção de mídia */}
              <div className="space-y-2">
                <Label>Imagem *</Label>
                <Tabs value={mediaSource} onValueChange={(v) => setMediaSource(v as 'gallery' | 'url')}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="gallery" className="gap-1.5">
                      <Image className="h-4 w-4" />
                      Galeria
                    </TabsTrigger>
                    <TabsTrigger value="url" className="gap-1.5">
                      <Link2 className="h-4 w-4" />
                      URL Externa
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="gallery" className="mt-2">
                    <div className="space-y-2">
                      {mediaUrl && !mediaUrl.startsWith('http') ? (
                        <div className="flex items-center gap-3 p-3 border rounded-lg">
                          <img
                            src={previewUrl || ''}
                            alt="Preview"
                            className="w-20 h-20 object-cover rounded-md"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{mediaFilename}</p>
                            <Button
                              variant="link"
                              size="sm"
                              className="px-0 h-auto"
                              onClick={() => setGalleryOpen(true)}
                            >
                              Trocar imagem
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          className="w-full h-24"
                          onClick={() => setGalleryOpen(true)}
                        >
                          <div className="flex flex-col items-center gap-1">
                            <Image className="h-6 w-6 text-muted-foreground" />
                            <span>Selecionar da Galeria</span>
                          </div>
                        </Button>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="url" className="mt-2">
                    <div className="space-y-2">
                      <Input
                        placeholder="https://exemplo.com/imagem.jpg"
                        value={mediaUrl.startsWith('http') ? mediaUrl : ''}
                        onChange={(e) => {
                          setMediaUrl(e.target.value);
                          setMediaFilename('');
                          setMediaMimeType('');
                        }}
                      />
                      {mediaUrl.startsWith('http') && (
                        <img
                          src={mediaUrl}
                          alt="Preview"
                          className="w-full max-h-40 object-contain rounded-md border"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </div>

              {/* Texto da mensagem */}
              <div className="space-y-2">
                <Label htmlFor="text-content">Texto da mensagem *</Label>
                <Textarea
                  id="text-content"
                  placeholder="Digite o texto que aparecerá junto com a imagem..."
                  value={textContent}
                  onChange={(e) => setTextContent(e.target.value)}
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">
                  Use <code className="bg-muted px-1 rounded">{'{{nome}}'}</code> para personalizar com o nome do contato
                </p>
              </div>

              {/* Lista de botões */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Botões de ação *</Label>
                  <span className="text-xs text-muted-foreground">
                    {buttonLabels.length}/{MAX_BUTTONS}
                  </span>
                </div>
                
                <div className="space-y-2">
                  {buttonLabels.map((label, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
                      <Input
                        placeholder={`Opção ${index + 1}`}
                        value={label}
                        onChange={(e) => handleUpdateButtonLabel(index, e.target.value)}
                        maxLength={20}
                        className="flex-1"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0"
                        onClick={() => handleRemoveButton(index)}
                        disabled={buttonLabels.length <= 1}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>

                {buttonLabels.length < MAX_BUTTONS && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={handleAddButton}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Adicionar opção
                  </Button>
                )}

                <p className="text-xs text-muted-foreground">
                  Máximo de {MAX_BUTTONS} botões. Cada botão pode ter até 20 caracteres.
                </p>
              </div>
            </div>
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={!isValid}>
              {initialValue ? 'Salvar' : 'Adicionar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de galeria */}
      <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Selecionar Imagem</DialogTitle>
          </DialogHeader>
          <MediaGalleryPicker
            onSelect={handleSelectFromGallery}
            onCancel={() => setGalleryOpen(false)}
            allowedTypes={['image']}
            currentSelection={undefined}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
